import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import UserHelperService from '../../services/UserHelperService';
import { Fragment } from 'react';
import { FormControl, TextField, Autocomplete, Chip, CircularProgress, Grid } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import FieldNames from '../../defaults/FieldNames.json';
import ErrorService from '../../services/ErrorService';

const BlueAlien = "#1976d2";

const useStyles = makeStyles({
  underline: {
    "&&&:before": {
      borderBottom: "2px solid red"
    },
    "&&:after": {
      borderBottom: "2px solid red"
    }
  },
  condensedFormControl: {
    margin: "5px 15px 5px 15px",
    minWidth: 120,
    width: '90%'
  },
  formControl: {
    margin: 15,
    minWidth: 120,
    width: '90%'
  },
  snackBarStyle: {
    top: '120px !important'
  },
});

const theme = createTheme({
  overrides: {
    MuiPaper: {
      root: {
        backgroundColor: 'green'
      }
    },
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          //initial border
          borderColor: BlueAlien,
        },
        '&:hover $notchedOutline': {
          //border on hover
          borderColor: BlueAlien,
          border: '2px solid'
        },
        '&$focused $notchedOutline': {
          //border on focus
          borderColor: BlueAlien,
          border: '2px solid'
        },
      },
    }
  },
});

export default function AdminDialogAssignment({userInfo, role, setRole, selectedRole, setSelectedRole, departmentSelection, setDepartmentSelection, agencySelection, setAgencySelection,
  regionSelection, setRegionSelection, unitSelection, setUnitSelection, subUnitSelection, setSubUnitSelection, currentlyUsedRegions, setCurrentlyUsedRegions, 
  currentlyUsedUnits, setCurrentlyUsedUnits, roleOptions, isTribalUser, setIsTribalUser, tribeSelection, setTribeSelection, setIsUserRoleDirty}) {
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [agencyOpen, setAgencyOpen] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const agencyLoading = agencyOpen && agencyOptions.length === 0;
  const [regionOpen, setRegionOpen] = useState(false);
  const [regionOptions, setRegionOptions] = useState([]);
  const regionLoading = regionOpen && regionOptions.length === 0;
  const [unitOpen, setUnitOpen] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const unitLoading = unitOpen && unitOptions.length === 0;
  const [subUnitOpen, setSubUnitOpen] = useState(false);
  const [subUnitOptions, setSubUnitOptions] = useState([]);
  const subUnitLoading = subUnitOpen && subUnitOptions.length === 0;
  const [tribeOpen, setTribeOpen] = useState(false);
  const [tribeOptions, setTribeOptions] = useState([]);
  const tribeLoading = tribeOpen && tribeOptions.length === 0;
  const config = useSelector((state) => state.configuration.config);
  const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);

  const classes = useStyles();

  useEffect(() => {
    setDepartmentSelection({Department: FieldNames.defaults.department});
    setDepartmentOptions([{Department: FieldNames.defaults.department}]);
  }, [setDepartmentSelection]);

  useEffect(() => {
    let active = true;

    if (!agencyLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(departmentSelection && departmentSelection.Department){
          UserHelperService.getAgencyByDepartment(departmentSelection.Department).then(response => {
            setAgencyOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          setAgencyOptions([{Region: ""}]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [agencyLoading, departmentSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!agencyOpen) {
      setAgencyOptions([]);
    }
  }, [agencyOpen]);

  useEffect(() => {
    let active = true;

    if (!regionLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(agencySelection && agencySelection.Agency){
          UserHelperService.getRegionsByAgency(agencySelection.Agency).then(response => {
            setRegionOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          setRegionOptions([{Region: ""}]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [regionLoading, agencySelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!regionOpen) {
      setRegionOptions([]);
    }
  }, [regionOpen]);

  useEffect(() => {
    let active = true;

    if (!unitLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(regionSelection && regionSelection.length > 0){
          const regionValues = regionSelection.map(x => x[FieldNames.units.region]);
          UserHelperService.getUnitsByRegionsAgency(regionValues, agencySelection.Agency).then(response => {
            setUnitOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          setUnitOptions([]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [unitLoading, regionSelection, config, rootServiceUrl, agencySelection]);

  useEffect(() => {
    if (!unitOpen) {
      setUnitOptions([]);
    }
  }, [unitOpen]);

  useEffect(() => {
    let active = true;

    if (!subUnitLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(unitSelection && unitSelection.length > 0){
          const unitValues = unitSelection.map(x => x[FieldNames.units.unit]);
          UserHelperService.getSubUnitsByUnits(unitValues).then(response => {
            setSubUnitOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          setSubUnitOptions([]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [subUnitLoading, unitSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!subUnitOpen) {
      setSubUnitOptions([]);
    }
  }, [subUnitOpen]);

  useEffect(() => {
    let active = true;

    if (!tribeLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(unitSelection && unitSelection.length > 0){
          const unitValues = unitSelection.map(x => x[FieldNames.units.unit]);
          UserHelperService.getTribesByUnits(unitValues).then(response => {
            setTribeOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else if(regionSelection && regionSelection.length > 0) {
          const regionValues = regionSelection.map(x => x[FieldNames.units.region]);
          UserHelperService.getTribesByRegions(regionValues).then(response => {
            setTribeOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          UserHelperService.getAllTribes().then(response => {
            setTribeOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [tribeLoading, unitSelection, regionSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!tribeOpen) {
      setTribeOptions([]);
    }
  }, [tribeOpen]);


  return (
    <Fragment>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12} container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Roles</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                value={role}
                options={roleOptions}
                getOptionLabel={(option) => {
                  return option ? option.name : ''
                }}
                getOptionDisabled={option => isTribalUser === FieldNames.yesNo.yes && option.name !== FieldNames.roles.local}
                isOptionEqualToValue={(option, value) => {
                  return option.code === value.code;
                }}
                onChange={(event, newValue) => {
                  if(newValue){
                    setRole(newValue);
                    setSelectedRole(newValue.name)
                  } else {
                    setRole(null);
                    setSelectedRole("")
                  }
                  setIsUserRoleDirty(true);
                }}
                inputValue={selectedRole}
                onInputChange={(event, newInputValue) => {
                  if(event){
                    setSelectedRole(newInputValue);
                    setIsUserRoleDirty(true);
                  } 
                  
                }}
                fullWidth
                renderInput={(params) => <TextField style={{ marginTop: '10px', paddingLeft: '0px', marginLeft: 0 }} {...params} />}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Department</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                id="departmentDemo"
                // value={departmentSelection}
                // disabled={(agencySelection) ? true : false }
                value={{Department: FieldNames.defaults.department}}
                disabled={true}
                onChange={(event, newValue) => {
                  setDepartmentSelection(newValue);
                  setIsUserRoleDirty(true);
                }}
                isOptionEqualToValue={(option, value) => option[FieldNames.units.department] === value[FieldNames.units.department]}
                getOptionLabel={(option) => { 
                  return option[FieldNames.units.department] 
                }}
                options={departmentOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Agency</label>
              </span>
            </Grid>
            <Grid item size={6}>
            <Autocomplete
                id="agencyDemo"
                value={agencySelection}
                disabled={(regionSelection && regionSelection.length > 0) ? true : false }
                onChange={(event, newValue) => {
                  if((!newValue && isTribalUser === FieldNames.yesNo.yes) || (newValue && newValue.Agency && !newValue.Agency.includes(FieldNames.agencies.bia))){
                    setIsTribalUser(FieldNames.yesNo.no);
                  }
                  setAgencySelection(newValue);
                  setIsUserRoleDirty(true);
                }}
                onOpen={() => {
                  setAgencyOpen(true);
                }}
                onClose={() => {
                  setAgencyOpen(false);
                }}
                isOptionEqualToValue={(option, value) => option[FieldNames.units.agency] === value[FieldNames.units.agency]}
                getOptionLabel={(option) => { 
                  return option[FieldNames.units.agency] 
                }}
                getOptionDisabled={option => isTribalUser === FieldNames.yesNo.yes && (option && !option[FieldNames.units.agency].includes(FieldNames.agencies.bia))}
                options={agencyOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Region</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="regionMulti"
                open={regionOpen}
                value={regionSelection}
                disableClearable
                onChange={(event, newValue) => {
                  setRegionSelection(newValue);
                  setIsUserRoleDirty(true);
                }}
                onOpen={() => {
                  setRegionOpen(true);
                }}
                onClose={() => {
                  setRegionOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { return option[FieldNames.units.region] === value[FieldNames.units.region] }}
                getOptionLabel={(option) => { 
                  return option[FieldNames.units.region] 
                }}
                options={regionOptions}
                getOptionDisabled={(option) => {
                  const selected = regionSelection.map(x => x[FieldNames.units.region]);
                  if(selected && selected.includes(option[FieldNames.units.region])){
                    return true;
                  } else {
                    return false;
                  }
                }}
                loading={regionLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option[FieldNames.units.region]}
                      {...getTagProps({ index })}
                      disabled={currentlyUsedRegions.length > 0 ? currentlyUsedRegions.indexOf(option[FieldNames.units.region]) !== -1 : false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {regionLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Unit</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="unitMulti"
                open={unitOpen}
                value={unitSelection}
                disableClearable
                onChange={(event, newValue) => {
                  setUnitSelection(newValue);
                  if(newValue && newValue.length > 0){
                    const allCurrentSelectedUnits = newValue.map(x => x[FieldNames.units.unit]);
                    UserHelperService.getUnitsWithRegionsByUnits(allCurrentSelectedUnits).then(response => {
                      const onlyRegions = response.map(x => x[FieldNames.units.region]);
                      setCurrentlyUsedRegions(onlyRegions);
                    }).catch(error => {
                      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                    });
                  } else {
                    setCurrentlyUsedRegions([]);
                  }
                  setIsUserRoleDirty(true);
                }}
                onOpen={() => {
                  setUnitOpen(true);
                }}
                onClose={() => {
                  setUnitOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { return option[FieldNames.units.unit] === value[FieldNames.units.unit] }}
                getOptionLabel={(option) => { 
                  return option[FieldNames.units.unit]
                }}
                options={unitOptions}
                getOptionDisabled={(option) => {
                  const selected = unitSelection.map(x => x[FieldNames.units.unit]);
                  if(selected && selected.includes(option[FieldNames.units.unit])){
                    return true;
                  } else {
                    return false;
                  }
                }}
                loading={unitLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option[FieldNames.units.unit]}
                      {...getTagProps({ index })}
                      disabled={currentlyUsedUnits.length > 0 ? currentlyUsedUnits.indexOf(option[FieldNames.units.unit]) !== -1 : false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {unitLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      {agencySelection && agencySelection.Agency === 'BLM' &&
        <FormControl variant="standard" className={classes.formControl}>
          <ThemeProvider theme={theme}>
            <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
              <Grid item size={6}>
                <span>
                  <label>SubUnit</label>
                </span>
              </Grid>
              <Grid item size={6}>
                <Autocomplete
                  multiple
                  id="subUnitMulti"
                  open={subUnitOpen}
                  value={subUnitSelection}
                  disableClearable
                  onChange={(event, newValue) => {
                    setSubUnitSelection(newValue);
                    if(newValue && newValue.length > 0 && agencySelection[FieldNames.units.agency] === 'BLM'){
                      const allCurrentSelectedSubUnits = newValue.map(x => x[FieldNames.units.subUnit]);
                      UserHelperService.getSubUnitsWithUnitsBySubUnits(allCurrentSelectedSubUnits).then(response => {
                        const onlyUnits = response.map(x => x[FieldNames.units.unit]);
                        setCurrentlyUsedUnits(onlyUnits);
                      }).catch(error => {
                        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                      });
                    } else {
                      setCurrentlyUsedUnits([]);
                    }
                    setIsUserRoleDirty(true);
                  }}
                  onOpen={() => {
                    setSubUnitOpen(true);
                  }}
                  onClose={() => {
                    setSubUnitOpen(false);
                  }}
                  isOptionEqualToValue={(option, value) => { return option[FieldNames.units.subUnit] === value[FieldNames.units.subUnit] }}
                  getOptionLabel={(option) => { 
                    return option[FieldNames.units.subUnit]
                  }}
                  options={subUnitOptions}
                  getOptionDisabled={(option) => {
                    const selected = subUnitSelection.map(x => x[FieldNames.units.subUnit]);
                    if(selected && selected.includes(option[FieldNames.units.subUnit])){
                      return true;
                    } else {
                      return false;
                    }
                  }}
                  loading={subUnitLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {subUnitLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params?.InputProps?.endAdornment}
                            </React.Fragment>
                          )
                        }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </ThemeProvider>
        </FormControl>
      }
      {isTribalUser === FieldNames.yesNo.yes &&
        <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12} container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Tribe</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                id="tribeSingular"
                open={tribeOpen}
                value={tribeSelection}
                onChange={(event, newValue) => {
                  setTribeSelection(newValue);
                  if(newValue){
                    UserHelperService.getRegionUnitWithTribe(newValue[FieldNames.units.tribeFullName]).then(resp => {
                      if(resp && resp.length > 0){
                        // We never get inside of an else. If there is a region/unit selected then the tribe is filtered by that option already
                        // Only the tribes that fall under the selected region are available for selection
                        if(!regionSelection || (regionSelection && regionSelection.length === 0)){
                          setRegionSelection([{Region: resp[0][FieldNames.units.region]}]);
                          //Since the region selection is empty set it to the newly selected one
                          setCurrentlyUsedRegions([resp[0][FieldNames.units.region]]);
                        } 
                        
                        if(!unitSelection || (unitSelection && unitSelection.length === 0)){
                          setUnitSelection([{Unit: resp[0][FieldNames.units.unit]}]);
                          //Since the unit selection is empty set it to the newly selected one
                          setCurrentlyUsedUnits([resp[0][FieldNames.units.unit]])
                        } 
                      }
                    }).catch(error => {
                      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
                    });
                  } else {
                    setCurrentlyUsedUnits([]);
                  }
                  setIsUserRoleDirty(true);
                }}
                onOpen={() => {
                  setTribeOpen(true);
                }}
                onClose={() => {
                  setTribeOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { 
                  if(option && value){
                    return option[FieldNames.units.tribeFullName] === value[FieldNames.units.tribeFullName];
                  } else {
                    return false;
                  }
                }}
                getOptionLabel={(option) => { 
                  return (option && option[FieldNames.units.tribeFullName]) ? option[FieldNames.units.tribeFullName] : '';
                }}
                options={tribeOptions}
                getOptionDisabled={(option) => {
                  if(tribeSelection === option[FieldNames.units.tribeFullName]){
                    return true;
                  } else {
                    return false;
                  }
                }}
                loading={tribeLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option[FieldNames.units.tribeFullName]}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {tribeLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      }
    </Fragment>
  );
}

AdminDialogAssignment.propTypes = {
  role: PropTypes.object,
  setRole: PropTypes.func,
  selectedRole: PropTypes.string,
  setSelectedRole: PropTypes.func,
  departmentSelection: PropTypes.object,
  setDepartmentSelection: PropTypes.func,
  agencySelection: PropTypes.object,
  setAgencySelection: PropTypes.func, 
  regionSelection: PropTypes.array,
  setRegionSelection: PropTypes.func, 
  unitSelection: PropTypes.array, 
  setUnitSelection: PropTypes.func,
  subUnitSelection: PropTypes.array, 
  setSubUnitSelection: PropTypes.func,
  currentlyUsedRegions: PropTypes.array,
  setCurrentlyUsedRegions: PropTypes.func,
  currentlyUsedUnits: PropTypes.array,
  setCurrentlyUsedUnits: PropTypes.func,
  roleOptions: PropTypes.array.isRequired,
  isTribalUser: PropTypes.string,
  setIsTribalUser: PropTypes.func,
  tribeSelection: PropTypes.object, 
  setTribeSelection: PropTypes.func
}